import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import AppLayout from "../AppLayout";
import { API } from "../apiConfig";

import {
  Plus,
  Search,
  Edit,
  Trash2,
  Save,
  X,
  ArrowLeft,
  Printer,
} from "lucide-react";
const today = () => new Date().toISOString().slice(0, 10);

const emptySale = {
  id: null,
  bill_no: "",
  bill_date: today(),
  bill_type: "NON_GST",
  customer_id: "",
  cash_amount: 0,
  upi_amount: 0,
  card_amount: 0,
  credit_amount: 0,
  discount_amount: 0,
  remarks: "",
};

const emptyItem = {
  product_id: "",
  product_name: "",
  qty: 1,
  rate: 0,
  mrp: 0,
  gst_percent: 0,
  discount_type: "NONE",
  discount_value: 0,
};

const selectStyle = {
  control: (base, state) => ({
    ...base,
    height: 56,
    minHeight: 56,
    borderRadius: 12,
    borderColor: state.isFocused ? "#2563eb" : "#cfe0f7",
    background: "#ffffff",
    boxShadow: state.isFocused ? "0 0 0 4px rgba(37,99,235,.13)" : "none",
    fontSize: 15,
    cursor: "pointer",
    "&:hover": {
      borderColor: "#2563eb",
    },
  }),

  valueContainer: (base) => ({
    ...base,
    height: 56,
    minHeight: 56,
    padding: "0 14px",
  }),

  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),

  placeholder: (base) => ({
    ...base,
    color: "#64748b",
    fontSize: 15,
  }),

  singleValue: (base) => ({
    ...base,
    color: "#111827",
    fontSize: 15,
  }),

  indicatorsContainer: (base) => ({
    ...base,
    height: 56,
  }),

  dropdownIndicator: (base) => ({
    ...base,
    padding: "0 12px",
  }),

  clearIndicator: (base) => ({
    ...base,
    padding: "0 12px",
  }),

  indicatorSeparator: (base) => ({
    ...base,
    height: 32,
    marginTop: 12,
    marginBottom: 12,
    backgroundColor: "#cbd5e1",
  }),

  menu: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

function Sale() {
  const [sales, setSales] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);

  const [form, setForm] = useState(emptySale);
  const [items, setItems] = useState([]);
  const [item, setItem] = useState(emptyItem);

  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [loading, setLoading] = useState(false);
  const [nextItemKey, setNextItemKey] = useState(1);

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const authHeader = useCallback(
    () => ({
      headers: {
        Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
      },
    }),
    []
  );

  const loadSales = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);

        const res = await axios.get(`${API}/sales`, {
          ...authHeader(),
          params: {
            page,
            per_page: pagination.per_page,
            search,
            from_date: fromDate,
            to_date: toDate,
          },
        });

        const data = res.data.data;

        setSales(data.data || []);
        setPagination({
          current_page: data.current_page,
          last_page: data.last_page,
          per_page: data.per_page,
          total: data.total,
        });
      } catch {
        Swal.fire("Error", "Sales loading failed", "error");
      } finally {
        setLoading(false);
      }
    },
    [authHeader, fromDate, toDate, pagination.per_page, search]
  );

  useEffect(() => {
    loadSales(1);
    loadDropdowns();
  }, [loadSales]);

  const totals = useMemo(() => {
    const gross_amount = items.reduce(
      (sum, row) => sum + Number(row.qty || 0) * Number(row.rate || 0),
      0
    );

    const gst_amount = items.reduce(
      (sum, row) => sum + Number(row.gst_amount || 0),
      0
    );

    const sub_total = gross_amount + gst_amount;
    const discount_amount = Number(form.discount_amount || 0);
    const after_discount = Math.max(sub_total - discount_amount, 0);
    const net_amount = Math.round(after_discount);
    const round_off = net_amount - after_discount;

    return {
      gross_amount,
      gst_amount,
      sub_total,
      discount_amount,
      round_off,
      net_amount,
    };
  }, [items, form.discount_amount]);

  const paidAmount =
    Number(form.cash_amount || 0) +
    Number(form.upi_amount || 0) +
    Number(form.card_amount || 0);

  const pendingAmount = Math.max(Number(totals.net_amount || 0) - paidAmount, 0);

  const indexSummary = useMemo(() => {
    return sales.reduce(
      (sum, row) => {
        sum.total_bill += Number(row.net_amount || 0);
        sum.cash += Number(row.cash_amount || 0);
        sum.upi += Number(row.upi_amount || 0);
        sum.card += Number(row.card_amount || 0);
        sum.pending += Number(row.credit_amount || 0);
        return sum;
      },
      { total_bill: 0, cash: 0, upi: 0, card: 0, pending: 0 }
    );
  }, [sales]);

  const customerOptions = useMemo(() => {
    return [
      { value: "", label: "Walk-in Customer" },
      ...customers.map((c) => ({
        value: c.id,
        label: `${c.customer_name}${c.mobile_no ? ` - ${c.mobile_no}` : ""}`,
      })),
    ];
  }, [customers]);

  const productOptions = useMemo(() => {
    return products.map((p) => ({
      value: p.id,
      label: `${p.product_name} | ₹${Number(p.sale_rate || 0).toFixed(2)}`,
    }));
  }, [products]);

  const productMap = useMemo(
    () => new Map(products.map((p) => [String(p.id), p])),
    [products]
  );

  const selectedCustomer = useMemo(
    () =>
      customerOptions.find((x) => String(x.value) === String(form.customer_id)) ||
      null,
    [customerOptions, form.customer_id]
  );

  const selectedProduct = useMemo(() => {
    if (item.product_id) {
      return (
        productOptions.find((x) => String(x.value) === String(item.product_id)) ||
        null
      );
    }

    if (item.product_name) {
      return {
        value: item.product_name,
        label: item.product_name,
        __isNew__: true,
      };
    }

    return null;
  }, [item.product_id, item.product_name, productOptions]);

  const createProduct = useCallback(
    async (name, row) => {
      const payload = {
        product_name: name.trim(),
        hsn_code: null,
        sale_rate: Number(row.rate || 0),
        mrp: Number(row.mrp || 0),
        gst_percent: Number(row.gst_percent || 0),
        discount_type: "NONE",
        discount_value: 0,
        is_active: 1,
      };

      const res = await axios.post(`${API}/products`, payload, authHeader());
      const newProduct = {
        id: res.data.id,
        ...payload,
      };

      setProducts((prev) => [...prev, newProduct]);
      return newProduct;
    },
    [authHeader]
  );

  const loadDropdowns = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sales-dropdowns`, authHeader());
      setCustomers(res.data.data.customers || []);
      setProducts(res.data.data.products || []);
    } catch (error) {
      console.log(error);
    }
  }, [authHeader]);

  const loadNextBillNo = async () => {
    try {
      const res = await axios.get(`${API}/sales-next-bill-no`, authHeader());
      return res.data.bill_no || "";
    } catch {
      return "";
    }
  };

  const openAddForm = async () => {
    const billNo = await loadNextBillNo();

    setForm({
      ...emptySale,
      bill_no: billNo,
      bill_date: today(),
    });

    setItems([]);
    setItem(emptyItem);
    setEditId(null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeForm = () => {
    setForm(emptySale);
    setItems([]);
    setItem(emptyItem);
    setEditId(null);
    setShowForm(false);
  };

  const handleSaleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleCustomerChange = useCallback((selected) => {
    setForm((prev) => ({ ...prev, customer_id: selected?.value || "" }));
  }, []);

  const handleProductChange = useCallback(
    (selected) => {
      if (!selected) {
        setItem(emptyItem);
        return;
      }

      const product = productMap.get(String(selected.value));

      if (product) {
        setItem({
          product_id: product.id,
          product_name: product.product_name,
          qty: 1,
          rate: Number(product.sale_rate || 0),
          mrp: Number(product.mrp || 0),
          gst_percent: Number(product.gst_percent || 0),
          discount_type: "NONE",
          discount_value: 0,
        });
        return;
      }

      setItem({
        product_id: "",
        product_name: selected.value,
        qty: 1,
        rate: 0,
        mrp: 0,
        gst_percent: 0,
        discount_type: "NONE",
        discount_value: 0,
      });
    },
    [productMap]
  );

  const handleItemChange = useCallback((e) => {
    const { name, value } = e.target;
    setItem((prev) => ({ ...prev, [name]: value }));
  }, []);

  const calculateItem = (row) => {
    const qty = Number(row.qty || 0);
    const rate = Number(row.rate || 0);
    const gross = qty * rate;

    const gstAmount =
      form.bill_type === "GST"
        ? (gross * Number(row.gst_percent || 0)) / 100
        : 0;

    return {
      discountAmount: 0,
      taxableAmount: gross,
      gstAmount,
      amount: gross + gstAmount,
    };
  };

  const currentItemCalc = calculateItem(item);

  const addItem = useCallback(async () => {
    if (!item.product_name) {
      Swal.fire("Required", "Please select product", "warning");
      return;
    }

    if (Number(item.qty || 0) <= 0) {
      Swal.fire("Required", "Please enter quantity", "warning");
      return;
    }

    let currentItem = { ...item };

    if (!currentItem.product_id) {
      const existing = products.find(
        (p) =>
          String(p.product_name).trim().toLowerCase() ===
          String(currentItem.product_name).trim().toLowerCase()
      );

      if (existing) {
        currentItem.product_id = existing.id;
      } else {
        const newProduct = await createProduct(currentItem.product_name, currentItem);
        currentItem.product_id = newProduct.id;
      }
    }

    const calc = calculateItem(currentItem);

    setItems((prev) => [
      ...prev,
      {
        ...currentItem,
        item_key: nextItemKey,
        qty: Number(currentItem.qty || 0),
        rate: Number(currentItem.rate || 0),
        mrp: Number(currentItem.mrp || 0),
        gst_percent: Number(currentItem.gst_percent || 0),
        discount_type: "NONE",
        discount_value: 0,
        discount_amount: 0,
        taxable_amount: calc.taxableAmount,
        gst_amount: calc.gstAmount,
        amount: calc.amount,
      },
    ]);

    setNextItemKey((prev) => prev + 1);
    setItem(emptyItem);
  }, [createProduct, item, nextItemKey, products]);

  const removeItem = useCallback((itemKey) => {
    setItems((prev) => prev.filter((row) => row.item_key !== itemKey));
  }, []);

  const setFullPayment = (type) => {
    setForm((prev) => ({
      ...prev,
      cash_amount: type === "cash" ? totals.net_amount : 0,
      upi_amount: type === "upi" ? totals.net_amount : 0,
      card_amount: type === "card" ? totals.net_amount : 0,
    }));
  };

  const openPrintBill = (saleId) => {
    if (!saleId) return;
    window.open(`/print-sale/${saleId}`, "_blank");
  };

  const saveSale = async (e) => {
    e.preventDefault();

    if (items.length === 0) {
      Swal.fire("Required", "Please add at least one product", "warning");
      return;
    }

    try {
      const payload = {
        ...form,
        customer_id: form.customer_id || null,

        gross_amount: totals.gross_amount,
        discount_amount: Number(form.discount_amount || 0),
        taxable_amount: totals.gross_amount,
        gst_amount: totals.gst_amount,
        round_off: totals.round_off,
        net_amount: totals.net_amount,

        cash_amount: Number(form.cash_amount || 0),
        upi_amount: Number(form.upi_amount || 0),
        card_amount: Number(form.card_amount || 0),
        credit_amount: pendingAmount,

        items,
      };

      let savedId = editId;

      if (editId) {
        await axios.put(`${API}/sales/${editId}`, payload, authHeader());
      } else {
        const res = await axios.post(`${API}/sales`, payload, authHeader());
        savedId = res.data.id;
      }

      const result = await Swal.fire({
        title: editId ? "Sale Updated" : "Sale Saved",
        text: "Do you want to print this bill?",
        icon: "success",
        showCancelButton: true,
        confirmButtonText: "Yes, Print",
        cancelButtonText: "No",
      });

      closeForm();
      loadSales(1);

      if (result.isConfirmed) {
        openPrintBill(savedId);
      }
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Something went wrong while saving sale";

      Swal.fire("Error", msg, "error");
    }
  };

  const editSale = async (id) => {
    try {
      const res = await axios.get(`${API}/sales/${id}`, authHeader());
      const data = res.data.data || {};
      const sale = data.sale || {};
      const details = data.items || [];

      setEditId(id);

      setForm({
        id: sale.id,
        bill_no: sale.bill_no || "",
        bill_date: String(sale.bill_date || "").slice(0, 10),
        bill_type: sale.bill_type || "NON_GST",
        customer_id: sale.customer_id || "",
        cash_amount: sale.cash_amount || 0,
        upi_amount: sale.upi_amount || 0,
        card_amount: sale.card_amount || 0,
        credit_amount: sale.credit_amount || 0,
        discount_amount: sale.discount_amount || 0,
        remarks: sale.remarks || "",
      });

      setItems(
        details.map((d, index) => ({
          product_id: d.product_id,
          product_name: d.product_name,
          qty: Number(d.qty || 0),
          rate: Number(d.rate || 0),
          mrp: Number(d.mrp || 0),
          gst_percent: Number(d.gst_percent || 0),
          discount_type: "NONE",
          discount_value: 0,
          discount_amount: 0,
          taxable_amount: Number(d.taxable_amount || 0),
          gst_amount: Number(d.gst_amount || 0),
          amount: Number(d.amount || 0),
          item_key: index + 1,
        }))
      );

      setNextItemKey(details.length + 1);
      setShowForm(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      Swal.fire("Error", "Sale loading failed", "error");
    }
  };

  const deleteSale = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete Sale?",
      text: "Stock and customer ledger will be reversed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${API}/sales/${id}`, authHeader());
      Swal.fire("Deleted", "Sale deleted successfully", "success");
      loadSales(pagination.current_page);
    } catch {
      Swal.fire("Error", "Sale delete failed", "error");
    }
  };

  return (
    <AppLayout
      title={showForm ? (editId ? "Edit Sale" : "New Sale") : "Sale Billing"}
      subtitle={showForm ? "Simple sale bill entry" : "Today collection and sale bills"}
    >
      {showForm ? (
        <div className="sale-form-page">
          <div className="sale-form-header">
            <div>
              <h3>{editId ? "Update Sale Bill" : "Create Sale Bill"}</h3>
              <p>Simple counter billing screen</p>
            </div>

            <button type="button" className="sale-icon-close" onClick={closeForm}>
              <X size={20} />
            </button>
          </div>

          <form onSubmit={saveSale}>
            <div className="sale-layout">
              <div className="sale-work-area">
                <div className="sale-top-card">
                  <div className="sale-top-grid">
                    <div className="sale-field">
                      <label>Customer</label>
                      <Select
                        styles={selectStyle}
                        value={selectedCustomer}
                        options={customerOptions}
                        onChange={handleCustomerChange}
                        placeholder="Select Customer"
                        isClearable
                      />
                    </div>

                    <div className="sale-field">
                      <label>Bill Date</label>
                      <input
                        type="date"
                        name="bill_date"
                        value={form.bill_date}
                        onChange={handleSaleChange}
                      />
                    </div>

                    <div className="sale-field">
                      <label>Bill No</label>
                      <input name="bill_no" value={form.bill_no} readOnly />
                    </div>

                    <div className="sale-field">
                      <label>Bill Type</label>
                      <select
                        name="bill_type"
                        value={form.bill_type}
                        onChange={handleSaleChange}
                      >
                        <option value="NON_GST">NON GST</option>
                        <option value="GST">GST</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="sale-items-title">Bill Items</div>

                <div className="sale-entry-row">
                  <div className="sale-field sale-product-col">
                    <label>Product</label>
                    <CreatableSelect
                      styles={selectStyle}
                      value={selectedProduct}
                      options={productOptions}
                      onChange={handleProductChange}
                      placeholder="Select or type product"
                      isClearable
                      formatCreateLabel={(inputValue) => `Add product: ${inputValue}`}
                    />
                  </div>

                  <div className="sale-field">
                    <label>Qty</label>
                    <input
                      type="number"
                      step="0.01"
                      name="qty"
                      value={item.qty}
                      onChange={handleItemChange}
                    />
                  </div>

                  <div className="sale-field">
                    <label>Rate</label>
                    <input
                      type="number"
                      step="0.01"
                      name="rate"
                      value={item.rate}
                      onChange={handleItemChange}
                    />
                  </div>

                  <div className="sale-field">
                    <label>GST %</label>
                    <input
                      type="number"
                      step="0.01"
                      name="gst_percent"
                      value={item.gst_percent}
                      onChange={handleItemChange}
                    />
                  </div>

                  <div className="sale-field">
                    <label>Total</label>
                    <input value={Number(currentItemCalc.amount || 0).toFixed(2)} readOnly />
                  </div>

                  <button type="button" className="sale-plus-btn" onClick={addItem}>
                    <Plus size={24} />
                  </button>
                </div>

                <div className="sale-table-box">
                  <table className="sale-items-table">
                    <thead>
                      <tr>
                        <th>PRODUCT</th>
                        <th>QTY</th>
                        <th>RATE</th>
                        <th>GST</th>
                        <th>TOTAL</th>
                        <th>ACTION</th>
                      </tr>
                    </thead>

                    <tbody>
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="sale-empty">
                            No items added
                          </td>
                        </tr>
                      ) : (
                        items.map((row) => (
                          <tr key={row.item_key}>
                            <td>{row.product_name}</td>
                            <td>{row.qty}</td>
                            <td>₹{Number(row.rate || 0).toFixed(2)}</td>
                            <td>₹{Number(row.gst_amount || 0).toFixed(2)}</td>
                            <td>₹{Number(row.amount || 0).toFixed(2)}</td>
                            <td>
                              <button
                                type="button"
                                className="sale-row-delete"
                                onClick={() => removeItem(row.item_key)}
                              >
                                <Trash2 size={18} />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="sale-summary-area">
                <div className="sale-summary-card">
                  <SummaryInput label="Bill Amount" value={totals.gross_amount} />
                  <SummaryInput label="GST" value={totals.gst_amount} />
                  <SummaryInput label="Sub Total" value={totals.sub_total} />

                  <div className="sale-summary-row">
                    <label>Discount:</label>
                    <input
                      type="number"
                      step="0.01"
                      name="discount_amount"
                      value={form.discount_amount}
                      onChange={handleSaleChange}
                    />
                  </div>

                  <SummaryInput label="Round Off" value={totals.round_off} />
                  <div className="sale-line" />
                  <SummaryInput label="Payable Amount" value={totals.net_amount} strong />
                </div>

                <div className="sale-pay-card">
                  <h4>Payment</h4>

                  <div className="sale-pay-short">
                    <button type="button" onClick={() => setFullPayment("cash")}>
                      Cash
                    </button>
                    <button type="button" onClick={() => setFullPayment("upi")}>
                      UPI
                    </button>
                    <button type="button" onClick={() => setFullPayment("card")}>
                      Card
                    </button>
                  </div>

                  <div className="sale-field">
                    <label>Cash</label>
                    <input
                      type="number"
                      step="0.01"
                      name="cash_amount"
                      value={form.cash_amount}
                      onChange={handleSaleChange}
                    />
                  </div>

                  <div className="sale-field">
                    <label>UPI</label>
                    <input
                      type="number"
                      step="0.01"
                      name="upi_amount"
                      value={form.upi_amount}
                      onChange={handleSaleChange}
                    />
                  </div>

                  <div className="sale-field">
                    <label>Card</label>
                    <input
                      type="number"
                      step="0.01"
                      name="card_amount"
                      value={form.card_amount}
                      onChange={handleSaleChange}
                    />
                  </div>

                  <div className="sale-field">
                    <label>Pending Amount</label>
                    <input value={pendingAmount.toFixed(2)} readOnly />
                  </div>
                </div>
              </div>
            </div>

            <div className="sale-action-bar">
              <button type="button" className="sale-back" onClick={closeForm}>
                <ArrowLeft size={18} />
                Back
              </button>

              <button type="submit" className="sale-save">
                <Save size={18} />
                {editId ? "Update Bill" : "Save Bill"}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="product-table-page">
          <div className="product-toolbar">
            <div className="product-search-box">
              <Search size={20} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") loadSales(1);
                }}
                placeholder="Search bill no / customer"
              />
            </div>

            <div className="sale-date-filter">
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
              <button
                type="button"
                className="product-add-btn"
                onClick={() => loadSales(1)}
              >
                Search
              </button>
            </div>

            <button className="product-add-btn" onClick={openAddForm}>
              <Plus size={20} />
              NEW SALE
            </button>
          </div>

          <div className="sale-index-summary">
            <CollectionCard title="Total Bill" value={indexSummary.total_bill} />
            <CollectionCard title="Cash" value={indexSummary.cash} />
            <CollectionCard title="UPI" value={indexSummary.upi} />
            <CollectionCard title="Card" value={indexSummary.card} />
            <CollectionCard title="Pending" value={indexSummary.pending} />
          </div>

          <div className="product-table-card">
            <div className="product-table-wrapper">
              <table className="product-simple-table">
                <thead>
                  <tr>
                    <th>Bill No</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Bill Amount</th>
                    <th>Cash</th>
                    <th>UPI</th>
                    <th>Card</th>
                    <th>Pending</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="product-empty-row">
                        Loading sales...
                      </td>
                    </tr>
                  ) : sales.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="product-empty-row">
                        No sales found
                      </td>
                    </tr>
                  ) : (
                    sales.map((sale) => (
                      <tr key={sale.id}>
                        <td>{sale.bill_no}</td>
                        <td>{String(sale.bill_date || "").slice(0, 10)}</td>
                        <td>{sale.customer_name || "Walk-in"}</td>
                        <td>₹{Number(sale.net_amount || 0).toFixed(2)}</td>
                        <td>₹{Number(sale.cash_amount || 0).toFixed(2)}</td>
                        <td>₹{Number(sale.upi_amount || 0).toFixed(2)}</td>
                        <td>₹{Number(sale.card_amount || 0).toFixed(2)}</td>
                        <td>₹{Number(sale.credit_amount || 0).toFixed(2)}</td>
                        <td>
                          <button
                            className="product-action-btn print"
                            onClick={() => openPrintBill(sale.id)}
                            title="Print Bill"
                          >
                            <Printer size={22} />
                          </button>

                          <button
                            className="product-action-btn edit"
                            onClick={() => editSale(sale.id)}
                            title="Edit Sale"
                          >
                            <Edit size={22} />
                          </button>

                          <button
                            className="product-action-btn delete"
                            onClick={() => deleteSale(sale.id)}
                            title="Delete Sale"
                          >
                            <Trash2 size={22} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="product-pagination">
              <span>
                {sales.length === 0
                  ? "0–0"
                  : `${(pagination.current_page - 1) * pagination.per_page + 1}–${
                      (pagination.current_page - 1) * pagination.per_page + sales.length
                    }`}{" "}
                of {pagination.total}
              </span>

              <button
                disabled={pagination.current_page <= 1}
                onClick={() => loadSales(pagination.current_page - 1)}
              >
                ‹
              </button>

              <button
                disabled={pagination.current_page >= pagination.last_page}
                onClick={() => loadSales(pagination.current_page + 1)}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

function SummaryInput({ label, value, strong }) {
  return (
    <div className={`sale-summary-row ${strong ? "strong" : ""}`}>
      <label>{label}:</label>
      <input value={Number(value || 0).toFixed(2)} readOnly />
    </div>
  );
}

function CollectionCard({ title, value }) {
  return (
    <div className="sale-collection-card">
      <span>{title}</span>
      <b>₹{Number(value || 0).toFixed(2)}</b>
    </div>
  );
}

export default Sale;